import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  APP_NAME, CONTACT_NOTE_MAX, LOCATION_MAX, SALE_DESCRIPTION_MAX, TITLE_MAX, type AdminSale,
} from '@sellit/shared';
import { api, ApiError } from '../lib/api';
import { setAppTitle } from '../lib/format';

export function SaleSettingsPage() {
  const { saleId = '' } = useParams();
  const [sale, setSale] = useState<AdminSale | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAppTitle('Sale Details');
    api.sale(saleId).then(setSale, (err: ApiError) => setError(err.message));
  }, [saleId]);

  if (error && !sale) return <main className="page narrow"><p className="error">{error}</p></main>;
  if (!sale) return <main className="page center muted">Loading…</main>;
  return <SettingsForm sale={sale} />;
}

function SettingsForm({ sale }: { sale: AdminSale }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: sale.title,
    publicLocation: sale.publicLocation,
    description: sale.description,
    contactNote: sale.contactNote ?? '',
    endsOn: sale.endsOn ?? '',
    status: sale.status,
  });
  // Publishing a draft or restoring an archived sale is done by the platform administrator.
  const statusLocked = sale.status === 'DRAFT' || sale.status === 'ARCHIVED';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const back = `/admin/sales/${sale.id}`;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      const { status, ...rest } = form;
      await api.updateSale(sale.id, {
        ...rest,
        endsOn: form.endsOn || null,
        ...(status === 'ACTIVE' || status === 'CLOSED' ? { status } : {}),
      });
      navigate(back);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save. Try again.');
      if (err instanceof ApiError && err.fields) setFieldErrors(err.fields);
      setBusy(false);
    }
  }

  return (
    <main className="page narrow">
      <header className="admin-header">
        <h1>Sale Details</h1>
        <Link className="link small" to={back}>Cancel</Link>
      </header>
      <p className="muted small">Everything here except the link is shown on your public sale page.</p>

      <form onSubmit={onSubmit} className="stack">
        <label className="field">
          <span>Sale name</span>
          <input value={form.title} maxLength={TITLE_MAX} onChange={(e) => set({ title: e.target.value })} />
          {fieldErrors.title && <small className="error">{fieldErrors.title}</small>}
        </label>

        <label className="field">
          <span>General area</span>
          <input value={form.publicLocation} maxLength={LOCATION_MAX} onChange={(e) => set({ publicLocation: e.target.value })} />
          <small className="muted">A neighborhood or city, never the street address.</small>
          {fieldErrors.publicLocation && <small className="error">{fieldErrors.publicLocation}</small>}
        </label>

        <label className="field">
          <span>How to buy</span>
          <textarea rows={3} maxLength={CONTACT_NOTE_MAX} value={form.contactNote} placeholder="e.g. Text 555-0100 with the item #. Cash or Venmo. Pickup only." onChange={(e) => set({ contactNote: e.target.value })} />
        </label>

        <label className="field">
          <span>Last day of the sale (optional)</span>
          <input type="date" value={form.endsOn} onChange={(e) => set({ endsOn: e.target.value })} />
          {fieldErrors.endsOn && <small className="error">{fieldErrors.endsOn}</small>}
        </label>

        <label className="field">
          <span>About the sale (optional)</span>
          <textarea rows={3} maxLength={SALE_DESCRIPTION_MAX} value={form.description} onChange={(e) => set({ description: e.target.value })} />
        </label>

        {statusLocked ? (
          <p className="notice small">
            {sale.status === 'DRAFT'
              ? `This sale is a draft: only you can see it. Your ${APP_NAME} administrator will publish it.`
              : 'This sale is archived and hidden from the public.'}
          </p>
        ) : (
        <>
        <fieldset className="segmented two" aria-label="Sale status">
          {(['ACTIVE', 'CLOSED'] as const).map((s) => (
            <label key={s} className={form.status === s ? 'on' : ''}>
              <input type="radio" name="status" value={s} checked={form.status === s} onChange={() => set({ status: s })} />
              {s === 'ACTIVE' ? 'Open' : 'Closed'}
            </label>
          ))}
        </fieldset>
        <p className="muted small">
          {form.status === 'CLOSED'
            ? 'Closed: buyers still see the page, marked as ended, and can no longer send inquiries.'
            : 'Open: buyers can browse and send inquiries.'}
        </p>
        </>
        )}

        {error && <p className="error" role="alert">{error}</p>}
        <button className="btn primary big" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
      </form>
    </main>
  );
}
